/**
 * API client for the Agent App backend.
 */

import type {
  NotesState,
  SyncTreeRequest,
  SyncResponse,
  AddNodeRequest,
  AddNodeResponse,
  AuthRequest,
  AuthResponse,
  SummaryResponse,
  KBSearchResponse,
  RelatedArticlesResponse,
  TreeNode,
  AttachmentDownloadResponse,
  AttachmentsResponse,
} from "./types";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

// =============================================================================
// Auth Token Management
// =============================================================================

const TOKEN_KEY = "notetaker_token";

export function getAuthToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
}

export function clearAuthToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

function getAuthHeaders(): HeadersInit {
  const token = getAuthToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

// =============================================================================
// Response Handler
// =============================================================================

export interface ApiError {
  detail: string;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      detail: `HTTP ${response.status}: ${response.statusText}`,
    }));
    throw new Error(error.detail);
  }
  return response.json();
}

// =============================================================================
// Auth API
// =============================================================================

export async function authenticateConnect(
  request: AuthRequest,
): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE}/api/auth/connect`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      signature: request.signature,
      agentArn: request.callerArn, // backend accepts both "agentArn" (alias) and "caller_arn"
      contactId: request.contactId,
    }),
  });
  const data = await handleResponse<AuthResponse>(response);
  if (data.success && data.token) {
    setAuthToken(data.token);
  }
  return data;
}

// =============================================================================
// LiveKit room lifecycle
// =============================================================================

export interface CreateLiveKitRoomRequest {
  contactId: string;
  agentArn?: string;
  instanceId?: string;
  channel?: string;
  /** Customer endpoint (phone for voice, email for chat). Surfaces in
   *  tools-ui's AI Search sidebar as a descriptive session label. */
  customerEndpoint?: string;
  /** Customer display name when available (typically chat). */
  customerName?: string;
}

export async function createLiveKitRoom(
  request: CreateLiveKitRoomRequest,
): Promise<{ success: boolean; room_name: string }> {
  const response = await fetch(`${API_BASE}/api/livekit/room`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      contactId: request.contactId,
      agentArn: request.agentArn ?? "",
      instanceId: request.instanceId ?? "",
      channel: request.channel ?? "voice",
      customerEndpoint: request.customerEndpoint ?? "",
      customerName: request.customerName ?? "",
    }),
  });
  return handleResponse(response);
}

export async function deleteLiveKitRoom(
  contactId: string,
  channel: string = "voice",
): Promise<{ success: boolean; room_name: string }> {
  const params = new URLSearchParams({ channel });
  const response = await fetch(
    `${API_BASE}/api/livekit/room/contact/${encodeURIComponent(contactId)}?${params}`,
    { method: "DELETE", headers: getAuthHeaders() },
  );
  return handleResponse(response);
}

// Notify the LiveKit room of a Connect contact-state transition.
// Server-side publishes a `contact.state-change` data packet to the
// room AND patches the room metadata with the matching
// `after_contact_work_{start,end}_timestamp` field. See the FastAPI
// route at calltree_agent_app/routers/livekit.py.
export async function postContactState(
  contactId: string,
  type: "starting_acw" | "cleared",
  at: number,
  channel: string = "voice",
): Promise<{ success: boolean; room_name: string; type: string }> {
  const response = await fetch(`${API_BASE}/api/livekit/room/contact-state`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ contactId, channel, type, at }),
  });
  return handleResponse(response);
}

// =============================================================================
// Health API
// =============================================================================

export async function getHealth(): Promise<{ status: string }> {
  const response = await fetch(`${API_BASE}/api/health`);
  return handleResponse(response);
}

// =============================================================================
// Related Contact Attachments API
// =============================================================================

export async function getRelatedContactAttachments(): Promise<AttachmentsResponse> {
  const response = await fetch(`${API_BASE}/api/attachments`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<AttachmentsResponse>(response);
}

export async function getAttachmentDownloadUrl(
  attachmentId: string,
): Promise<AttachmentDownloadResponse> {
  const response = await fetch(
    `${API_BASE}/api/attachments/${encodeURIComponent(attachmentId)}/download`,
    { headers: getAuthHeaders() },
  );
  return handleResponse<AttachmentDownloadResponse>(response);
}

// =============================================================================
// Notes API
// =============================================================================

export async function getNotes(contactId: string): Promise<NotesState> {
  const response = await fetch(`${API_BASE}/api/notes/${contactId}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function createNotes(
  contactId: string,
  calltreeTable: Array<{ name: string; description: string }> = [],
  caseId?: string,
): Promise<{ success: boolean; created: boolean; tree: TreeNode }> {
  const response = await fetch(`${API_BASE}/api/notes/create`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      contact_id: contactId,
      calltree_table: calltreeTable,
      case_id: caseId,
    }),
  });
  return handleResponse(response);
}

export async function syncTree(
  request: SyncTreeRequest,
): Promise<SyncResponse> {
  const response = await fetch(`${API_BASE}/api/notes/tree/sync`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });
  return handleResponse(response);
}

export async function addNode(
  request: AddNodeRequest,
): Promise<AddNodeResponse> {
  const response = await fetch(`${API_BASE}/api/notes/tree/add`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });
  return handleResponse(response);
}

export async function updateNode(
  nodeId: string,
  contactId: string,
  updates: { text?: string; attrs?: Record<string, unknown> },
): Promise<{ success: boolean; node: TreeNode; version: number }> {
  const response = await fetch(`${API_BASE}/api/notes/tree/node/${nodeId}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      contact_id: contactId,
      ...updates,
    }),
  });
  return handleResponse(response);
}

export async function deleteNode(
  nodeId: string,
  contactId: string,
): Promise<{ success: boolean; version: number }> {
  const response = await fetch(
    `${API_BASE}/api/notes/tree/node/${nodeId}?contact_id=${contactId}`,
    {
      method: "DELETE",
      headers: getAuthHeaders(),
    },
  );
  return handleResponse(response);
}

export async function updateContactStatus(
  contactId: string,
  status: "live" | "completed" = "completed",
): Promise<{ success: boolean; status: string }> {
  const response = await fetch(
    `${API_BASE}/api/notes/${contactId}/status?status=${status}`,
    {
      method: "POST",
      headers: getAuthHeaders(),
    },
  );
  return handleResponse(response);
}

export async function getContactSummary(
  contactId: string,
  regenerate = false,
): Promise<SummaryResponse> {
  const url = regenerate
    ? `${API_BASE}/api/notes/${contactId}/summary?regenerate=true`
    : `${API_BASE}/api/notes/${contactId}/summary`;
  const response = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

// =============================================================================
// Customer 360 — Durable Lambda invoke + poll
// =============================================================================

export interface C360InvokeResponse {
  execution_name: string;
  execution_arn: string;
  status: string;
}

export interface C360StepEvent {
  name: string;
  event_type: string;
  timestamp: string;
}

export interface C360StatusResponse {
  execution_name: string;
  execution_arn: string | null;
  status: string; // INVOKING | NOT_FOUND | RUNNING | SUCCEEDED | FAILED
  steps: C360StepEvent[];
  result: Record<string, unknown> | null;
  error: Record<string, unknown> | null;
}

export async function invokeCustomer360(
  contactId: string,
  query: string,
  mode: string = "short",
  channel?: string | null,
): Promise<C360InvokeResponse> {
  const response = await fetch(
    `${API_BASE}/api/customer360/${contactId}/invoke`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ query, mode, channel: channel ?? undefined }),
    },
  );
  return handleResponse<C360InvokeResponse>(response);
}

export async function pollCustomer360Status(
  contactId: string,
  executionArn: string,
): Promise<C360StatusResponse> {
  const response = await fetch(
    `${API_BASE}/api/customer360/${contactId}/status?execution_arn=${encodeURIComponent(executionArn)}`,
    { headers: getAuthHeaders() },
  );
  return handleResponse<C360StatusResponse>(response);
}

// =============================================================================
// Knowledge Base API
// =============================================================================

export async function searchKB(
  query: string,
  limit = 5,
): Promise<KBSearchResponse> {
  const response = await fetch(
    `${API_BASE}/api/kb/search?q=${encodeURIComponent(query)}&limit=${limit}`,
    {
      headers: getAuthHeaders(),
    },
  );
  return handleResponse(response);
}

export async function getRelatedArticles(
  contactId: string,
  limit = 5,
): Promise<RelatedArticlesResponse> {
  const response = await fetch(
    `${API_BASE}/api/kb/related/${contactId}?limit=${limit}`,
    {
      headers: getAuthHeaders(),
    },
  );
  return handleResponse(response);
}

// =============================================================================
// Customer 360 API
// =============================================================================

// =============================================================================
// Message Simulation API (local dev)
// =============================================================================

export async function simulateMessage(
  contactId: string,
  speaker: "CUSTOMER" | "AGENT",
  text: string,
): Promise<{
  success: boolean;
  message: { speaker: string; text: string; timestamp: string };
  version: number;
  patches: number;
}> {
  const response = await fetch(
    `${API_BASE}/api/notes/${contactId}/simulate-message`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ speaker, text }),
    },
  );
  return handleResponse(response);
}

// Legacy exports for backwards compatibility
export { syncTree as syncNotes };
export function createNotesStream(contactId: string): EventSource {
  const token = getAuthToken();
  const url = new URL(
    `${API_BASE}/api/notes/${contactId}/stream`,
    window.location.origin,
  );
  if (token) {
    url.searchParams.set("token", token);
  }
  return new EventSource(url.toString());
}
